import { isContractProcedure, oc, type AnyContractProcedure, type AnyContractRouter, type ContractRouterClient, type InferContractRouterInputs, type InferContractRouterOutputs } from '@orpc/contract';
import { z } from 'zod';
import {
  CHANNEL_TYPES,
  bindingTemplateSchema,
  BITRIX24_ENTITIES,
  bitrix24CatalogSchema,
  bitrix24CategoryIdSchema,
  bitrix24ChannelConfigSchema,
  bitrix24FieldMapSchema,
  bitrix24SourceIdSchema,
  bitrix24UserIdSchema,
  bitrix24WebhookUrlSchema,
  channelTypesSchema,
  emailChannelConfigSchema,
  webhookChannelConfigSchema,
  webhookHeaderNameSchema,
  webhookHeaderValueSchema,
  webhookUrlSchema,
} from './channels.ts';
import { fieldDefinitionSchema, fieldsSchema } from './fields.ts';
import { CAPTCHA_PROVIDERS, DEFAULT_CAPTCHA_MIN_SCORE, LOCALE_CODES, captchaMinScoreSchema, captchaSiteKeySchema, settingsOverrideSchema, settingsSchema } from './settings.ts';

// The tenant API — the contract between `apps/api` and the dashboard (spec §17.3). It is defined once,
// here: the server implements it, the browser gets a typed client from it, and a test walks it to
// prove that every procedure declares who may call it.
//
// Which tenant a call is about travels in the `x-tenant-id` header, not in each input: the dashboard
// works inside one tenant at a time, and the server checks the caller's membership of that tenant
// before anything else runs.

/** Tenant roles, weakest first (spec §4). A procedure names the weakest role that may call it. */
export const ROLES = ['viewer', 'member', 'admin', 'owner'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export const TENANT_HEADER = 'x-tenant-id';

export interface ApiMeta {
  /**
   * Who may call the procedure: `user` — anyone signed in, no tenant involved; a role — a member of
   * the tenant named in the request with at least that role. There is no default: a procedure
   * without `access` is refused by the server and fails the contract test.
   */
  access?: 'user' | Role;
  /**
   * For procedures that name a project (directly or through one of its forms or submissions):
   * a member limited to some projects (FR-4.3) may use it only within those.
   */
  projectScoped?: boolean;
}

const base = oc.$meta<ApiMeta>({}).errors({
  UNAUTHORIZED: { status: 401, message: 'Sign in to continue.' },
  FORBIDDEN: { status: 403, message: 'You do not have access to this.' },
  NOT_FOUND: { status: 404, message: 'Not found.' },
  CONFLICT: { status: 409, message: 'This conflicts with something that already exists.' },
  EMAIL_NOT_VERIFIED: { status: 403, message: 'Confirm your email address first.' },
  INPUT_VALIDATION_FAILED: {
    status: 422,
    message: 'Some values are not valid.',
    data: z.object({ formErrors: z.array(z.string()), fieldErrors: z.record(z.string(), z.array(z.string())) }),
  },
});

const id = z.uuid();

/** An IANA timezone name the runtime knows, e.g. `Europe/Berlin`. */
export const timezoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: value });
      return true;
    } catch {
      return false;
    }
  }, 'must be a timezone name such as Europe/Berlin');
const name = z.string().trim().min(1).max(200);

// --- settings -----------------------------------------------------------------------------------

/** What the dashboard may edit. CAPTCHA has its own flow because it involves a secret. */
export const editableSettingsSchema = settingsOverrideSchema.omit({ captcha: true }).strict();
export type EditableSettings = z.infer<typeof editableSettingsSchema>;

/** Settings as shown: everything, except that a CAPTCHA secret never leaves the server. */
export const shownSettingsSchema = settingsSchema.omit({ captcha: true }).extend({
  captcha: z.object({ provider: z.enum(CAPTCHA_PROVIDERS), siteKey: z.string().optional(), minScore: z.number().optional() }),
});
export type ShownSettings = z.infer<typeof shownSettingsSchema>;

/**
 * Setting up CAPTCHA (FR-9.5). The secret is write-only: it is sealed on arrival and never shown
 * again. Leaving it out keeps the secret already saved, which is possible only while the provider
 * stays the same.
 */
const captchaSecretSchema = z.string().trim().min(1).max(500);
export const captchaInputSchema = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('none') }),
  z.object({ provider: z.enum(['turnstile', 'recaptcha_v2']), siteKey: captchaSiteKeySchema, secret: captchaSecretSchema.optional() }),
  z.object({ provider: z.literal('recaptcha_v3'), siteKey: captchaSiteKeySchema, secret: captchaSecretSchema.optional(), minScore: captchaMinScoreSchema.default(DEFAULT_CAPTCHA_MIN_SCORE) }),
]);
export type CaptchaInput = z.infer<typeof captchaInputSchema>;

const settingSource = z.enum(['default', 'project', 'form']);

// --- shapes -------------------------------------------------------------------------------------

const tenantSchema = z.object({
  id,
  slug: z.string(),
  name: z.string(),
  region: z.string(),
  status: z.enum(['active', 'suspended']),
  locale: z.string(),
  timezone: z.string(),
  /** The channel types this workspace may create (spec §19 #67). Staff set them; the dashboard offers only these. */
  channelTypes: channelTypesSchema,
});

const projectSchema = z.object({
  id,
  slug: z.string(),
  name: z.string(),
  createdAt: z.date(),
  formCount: z.number().int(),
  /** Only the keys the project sets itself. */
  overrides: editableSettingsSchema,
  /** Platform defaults with the project's own values applied. */
  settings: shownSettingsSchema,
});

const formSummarySchema = z.object({
  id,
  projectId: id,
  slug: z.string(),
  key: z.string(),
  name: z.string(),
  status: z.enum(['active', 'paused']),
  createdAt: z.date(),
  recentSubmissions: z.number().int(),
  lastSubmissionAt: z.date().nullable(),
});

const formDetailSchema = z.object({
  id,
  slug: z.string(),
  key: z.string(),
  name: z.string(),
  status: z.enum(['active', 'paused']),
  schemaVersion: z.number().int(),
  createdAt: z.date(),
  project: z.object({ id, name: z.string() }),
  fields: z.array(fieldDefinitionSchema),
  /** Only the keys the form overrides (FR-5.2). */
  overrides: editableSettingsSchema,
  /** The values in force, and where each one comes from. */
  settings: shownSettingsSchema,
  provenance: z.record(z.string(), settingSource),
  /** The public endpoint and ready-to-paste integration code (FR-5.14). */
  endpoint: z.string(),
  /** `client` — markup for our JS client; `html` — a plain form, no script of ours; `fetch` — for a page that submits by itself. */
  snippets: z.object({ client: z.string(), html: z.string(), fetch: z.string() }),
});

const channelBase = {
  id,
  slug: z.string(),
  name: z.string(),
  /** `unverified` until a notification has been delivered through it (FR-8.3). */
  healthState: z.string(),
  consecutiveFailures: z.number().int(),
  lastOkAt: z.date().nullable(),
  createdAt: z.date(),
};

/** A channel as shown. Secrets never are: a webhook's signing secret and header values, a Bitrix24 webhook's URL. */
const channelSchema = z.discriminatedUnion('type', [
  z.object({ ...channelBase, type: z.literal('email'), config: emailChannelConfigSchema }),
  z.object({ ...channelBase, type: z.literal('webhook'), config: webhookChannelConfigSchema }),
  z.object({ ...channelBase, type: z.literal('bitrix24'), config: bitrix24ChannelConfigSchema }),
]);

/** A custom webhook header as typed. On an update a header without a value keeps the one stored. */
const webhookHeaderInput = z.object({ name: webhookHeaderNameSchema, value: webhookHeaderValueSchema.optional() });

/** The signing secret, in the one answer that shows it: the one that created or rotated it. */
const withSigningSecret = z.object({ channel: channelSchema, signingSecret: z.string() });

export const bindingStateSchema = z.enum(['bound', 'disabled', 'none']);

const bindingRowSchema = z.object({
  channelId: id,
  /** Whether the project sends to this channel. */
  project: z.enum(['bound', 'none']),
  /** What the form says about it; absent when the question was asked about a project only. */
  form: bindingStateSchema.optional(),
  /** Whether a submission to this form (or, for a project, to a form that changes nothing) is sent there. */
  effective: z.boolean(),
  template: bindingTemplateSchema.nullable(),
});

const fieldValue = z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]);
export const submissionStatusSchema = z.enum(['received', 'spam', 'deleted', 'pending_verification', 'quarantined']);
const deliveryStatusSchema = z.enum(['pending', 'sending', 'sent', 'dead', 'suppressed']);

const submissionRowSchema = z.object({
  id,
  createdAt: z.date(),
  projectId: id,
  formId: id,
  formName: z.string(),
  status: submissionStatusSchema,
  spamReason: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /** The first lines of what was sent, for the list. */
  preview: z.string(),
  deliveries: z.array(z.object({ status: deliveryStatusSchema })),
});

const submissionDetailSchema = z.object({
  id,
  createdAt: z.date(),
  project: z.object({ id, name: z.string() }),
  form: z.object({ id, name: z.string() }),
  status: submissionStatusSchema,
  spamReason: z.string().nullable(),
  /** Values in the order of the schema they were validated against, with their labels. */
  values: z.array(z.object({ key: z.string(), label: z.string(), value: fieldValue })),
  extra: z.record(z.string(), fieldValue),
  meta: z.object({
    ip: z.string().optional(),
    userAgent: z.string().optional(),
    origin: z.string().optional(),
    referer: z.string().optional(),
    page: z.record(z.string(), z.string()).optional(),
    captcha: z.enum(['verified', 'unverified']).optional(),
  }),
  deliveries: z.array(
    z.object({
      id,
      channelId: id,
      channelName: z.string(),
      channelType: z.enum(CHANNEL_TYPES),
      /** FR-12.11 — the channel was not brought along when the project moved here: it holds this history only. */
      channelRetired: z.boolean(),
      status: deliveryStatusSchema,
      attempts: z.number().int(),
      lastError: z.string().nullable(),
      nextAttemptAt: z.date().nullable(),
      sentAt: z.date().nullable(),
    }),
  ),
});

const memberSchema = z.object({
  memberId: z.string(),
  userId: z.string(),
  email: z.string(),
  name: z.string(),
  role: roleSchema,
  /** The projects this membership is limited to; `null` means the whole tenant (FR-4.3). */
  projectIds: z.array(id).nullable(),
  joinedAt: z.date(),
});

const invitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: roleSchema,
  expiresAt: z.date(),
  projectIds: z.array(id).nullable(),
});

const ok = z.object({ ok: z.literal(true) });

// --- the contract -------------------------------------------------------------------------------

export const apiContract = {
  me: {
    /** The signed-in user and the tenants they belong to. */
    get: base.meta({ access: 'user' }).output(
      z.object({
        user: z.object({ id: z.string(), email: z.string(), name: z.string(), emailVerified: z.boolean() }),
        memberships: z.array(z.object({ tenantId: id, organizationId: z.string(), tenantSlug: z.string(), tenantName: z.string(), role: roleSchema })),
      }),
    ),
  },

  tenant: {
    get: base.meta({ access: 'viewer' }).output(tenantSchema.extend({ role: roleSchema, projectIds: z.array(id).nullable() })),
    update: base
      .meta({ access: 'admin' })
      .input(z.object({ name: name.optional(), locale: z.enum(LOCALE_CODES).optional(), timezone: timezoneSchema.optional() }))
      .output(tenantSchema),
    /** The numbers on the first screen. */
    overview: base.meta({ access: 'viewer' }).output(
      z.object({
        submissions: z.object({ last24h: z.number().int(), last7d: z.number().int(), spam7d: z.number().int() }),
        deliveries: z.object({ dead: z.number().int(), pending: z.number().int() }),
        projects: z.number().int(),
        forms: z.number().int(),
        channels: z.number().int(),
      }),
    ),
  },

  projects: {
    list: base.meta({ access: 'viewer' }).output(z.array(projectSchema)),
    get: base.meta({ access: 'viewer', projectScoped: true }).input(z.object({ id })).output(projectSchema),
    create: base.meta({ access: 'member' }).input(z.object({ name, settings: editableSettingsSchema.default({}) })).output(projectSchema),
    update: base
      .meta({ access: 'member', projectScoped: true })
      .input(z.object({ id, name: name.optional(), settings: editableSettingsSchema.optional() }))
      .output(projectSchema),
    /** Sets the CAPTCHA every form of the project uses unless the form says otherwise. */
    setCaptcha: base.meta({ access: 'member', projectScoped: true }).input(z.object({ id, captcha: captchaInputSchema })).output(projectSchema),
    delete: base.meta({ access: 'admin', projectScoped: true }).input(z.object({ id })).output(ok),
  },

  forms: {
    list: base.meta({ access: 'viewer' }).input(z.object({ projectId: id.optional() })).output(z.array(formSummarySchema)),
    get: base.meta({ access: 'viewer', projectScoped: true }).input(z.object({ id })).output(formDetailSchema),
    /** Creates a form from a starter template or from a field list (FR-5.12). Needs a confirmed email (FR-4.4). */
    create: base
      .meta({ access: 'member', projectScoped: true })
      .input(
        z.object({
          projectId: id,
          name,
          template: z.enum(['contact', 'callback', 'newsletter']).optional(),
          fields: fieldsSchema.optional(),
        }),
      )
      .output(formDetailSchema),
    /**
     * `settings` replaces the form's whole set of overrides: a key left out goes back to inherited
     * (FR-5.2). `fields` adds a schema version (FR-5.10).
     */
    update: base
      .meta({ access: 'member', projectScoped: true })
      .input(z.object({ id, name: name.optional(), status: z.enum(['active', 'paused']).optional(), settings: editableSettingsSchema.optional(), fields: fieldsSchema.optional() }))
      .output(formDetailSchema),
    /** Sets the form's own CAPTCHA — `none` switches it off for this form; `null` goes back to the project's (FR-5.2). */
    setCaptcha: base.meta({ access: 'member', projectScoped: true }).input(z.object({ id, captcha: captchaInputSchema.nullable() })).output(formDetailSchema),
    delete: base.meta({ access: 'admin', projectScoped: true }).input(z.object({ id })).output(ok),
  },

  // Creating a channel of a type the workspace does not have on (`tenant.channelTypes`) is refused
  // with 403; a channel it has already goes on working, and can still be edited, bound and deleted.
  channels: {
    list: base.meta({ access: 'member' }).output(z.array(channelSchema)),
    /** An email channel (FR-8.5). */
    create: base.meta({ access: 'member' }).input(z.object({ name, config: emailChannelConfigSchema })).output(channelSchema),
    /** Renames any channel; `config` is an email channel's (a webhook has its own procedure). */
    update: base.meta({ access: 'member' }).input(z.object({ id, name: name.optional(), config: emailChannelConfigSchema.optional() })).output(channelSchema),
    delete: base.meta({ access: 'admin' }).input(z.object({ id })).output(ok),
    /**
     * FR-8.7 — a webhook channel. The platform makes its signing secret and shows it in this answer
     * only; header values are sealed on arrival and never shown again.
     */
    createWebhook: base
      .meta({ access: 'member' })
      .input(z.object({ name, url: webhookUrlSchema, headers: z.array(webhookHeaderInput.required({ value: true })).max(10).default([]) }))
      .output(withSigningSecret),
    /** `headers` lists every header from now on: one without a value keeps its stored value; one left out is removed. */
    updateWebhook: base
      .meta({ access: 'member' })
      .input(z.object({ id, name: name.optional(), url: webhookUrlSchema.optional(), headers: z.array(webhookHeaderInput).max(10).optional() }))
      .output(channelSchema),
    /** A new signing secret, shown in this answer only. The old one keeps signing for a day, so the receiver can switch. */
    rotateWebhookSecret: base.meta({ access: 'member' }).input(z.object({ id })).output(withSigningSecret),
    /**
     * Spec §19 #66 — a Bitrix24 channel: every submission becomes a lead, or (`entity: deal`, §19 #75) a
     * deal with a contact. `webhookUrl` is the portal's inbound webhook; its code is a credential, so it
     * is sealed on arrival and never shown again. The webhook is tried (it must have the CRM permission,
     * and work for what the channel creates) before anything is saved. `entity` cannot be changed later.
     */
    createBitrix24: base
      .meta({ access: 'member' })
      .input(
        z.object({
          name,
          webhookUrl: bitrix24WebhookUrlSchema,
          entity: z.enum(BITRIX24_ENTITIES).default('lead'),
          assignedById: bitrix24UserIdSchema.nullable().default(null),
          sourceId: bitrix24SourceIdSchema.default('WEB'),
        }),
      )
      .output(channelSchema),
    /**
     * `webhookUrl` replaces the stored one — it is tried first, like a new one; left out, the stored one
     * stays. `fieldMap` replaces the mapping (§19 #68); each target must be a field of the portal as last
     * read. `categoryId` — a deal channel's pipeline (§19 #75), one of the portal's as last read; `null`,
     * the default one.
     */
    updateBitrix24: base
      .meta({ access: 'member' })
      .input(
        z.object({
          id,
          name: name.optional(),
          webhookUrl: bitrix24WebhookUrlSchema.optional(),
          assignedById: bitrix24UserIdSchema.nullable().optional(),
          sourceId: bitrix24SourceIdSchema.optional(),
          fieldMap: bitrix24FieldMapSchema.optional(),
          categoryId: bitrix24CategoryIdSchema.nullable().optional(),
        }),
      )
      .output(channelSchema),
    /**
     * §19 #68 — what the mapping editor needs: the portal's lead or deal fields (and a deal channel's
     * pipelines) as last read — `null` until the first read — and the fields of the workspace's forms, by key.
     */
    bitrix24Mapping: base.meta({ access: 'member' }).input(z.object({ id })).output(
      z.object({
        catalog: bitrix24CatalogSchema.nullable(),
        formFields: z.array(
          z.object({
            key: z.string(),
            label: z.string(),
            type: z.string(),
            role: z.string().nullable(),
            /** A select's options, from every form that has the field. */
            options: z.array(z.string()),
            /** The forms that have a field with this key. */
            forms: z.array(z.string()),
          }),
        ),
      }),
    ),
    /** Asks the worker to read the portal's lead fields again. The answer's catalog has `requestedAt` set until it is done. */
    refreshBitrix24Fields: base.meta({ access: 'member' }).input(z.object({ id })).output(z.object({ catalog: bitrix24CatalogSchema })),
  },

  bindings: {
    /** Every channel of the tenant, with how it relates to the project and, if given, the form (FR-8.1). */
    list: base.meta({ access: 'member', projectScoped: true }).input(z.object({ projectId: id, formId: id.optional() })).output(z.array(bindingRowSchema)),
    set: base
      .meta({ access: 'member', projectScoped: true })
      .input(z.object({ scope: z.object({ type: z.enum(['project', 'form']), id }), channelId: id, state: bindingStateSchema, template: bindingTemplateSchema.nullable().optional() }))
      .output(ok),
  },

  submissions: {
    list: base
      .meta({ access: 'viewer' })
      .input(
        z.object({
          projectId: id.optional(),
          formId: id.optional(),
          status: submissionStatusSchema.optional(),
          search: z.string().trim().max(200).optional(),
          /** The `nextCursor` of the previous page. */
          cursor: z.string().max(200).optional(),
          limit: z.number().int().min(1).max(100).default(25),
        }),
      )
      .output(z.object({ items: z.array(submissionRowSchema), nextCursor: z.string().nullable() })),
    get: base.meta({ access: 'viewer', projectScoped: true }).input(z.object({ id })).output(submissionDetailSchema),
    /** FR-7.10 */
    delete: base.meta({ access: 'admin', projectScoped: true }).input(z.object({ id })).output(ok),
    /** FR-7.7 — sends a failed (or stuck) notification again. */
    resend: base.meta({ access: 'member', projectScoped: true }).input(z.object({ deliveryId: id })).output(ok),
  },

  members: {
    list: base.meta({ access: 'admin' }).output(z.array(memberSchema)),
    /** Invitations that have not been answered or withdrawn. */
    invitations: base.meta({ access: 'admin' }).output(z.array(invitationSchema)),
    /**
     * FR-4.5 — invites an address with a role and, optionally, a limit to some projects. The limit is
     * part of the invitation, so it applies from the moment the person joins.
     */
    invite: base
      .meta({ access: 'admin' })
      .input(z.object({ email: z.email().max(254), role: roleSchema, projectIds: z.array(id).max(200).default([]) }))
      .output(z.object({ id: z.string() })),
    /** FR-4.3 — limits a membership to some projects; an empty list removes the limit. */
    setProjects: base.meta({ access: 'admin' }).input(z.object({ memberId: z.string().min(1).max(100), projectIds: z.array(id).max(200) })).output(ok),
  },
};

export type ApiContract = typeof apiContract;
export type ApiClient = ContractRouterClient<ApiContract>;
export type ApiInputs = InferContractRouterInputs<ApiContract>;
export type ApiOutputs = InferContractRouterOutputs<ApiContract>;

/** Every procedure of a contract with its dotted path — for tests that must not miss one. */
export function listProcedures(router: AnyContractRouter, path: readonly string[] = []): { path: readonly string[]; procedure: AnyContractProcedure }[] {
  if (isContractProcedure(router)) return [{ path, procedure: router }];
  // cast: a contract router that is not a procedure is an object whose values are contract routers.
  return Object.entries(router as Record<string, AnyContractRouter>).flatMap(([key, child]) => listProcedures(child, [...path, key]));
}
